/**
 * RankedBars — the DrillExplorer bar-row pattern as a reusable card body
 * (2026-06-12 card audit: 6+ cards across Exec/Marketing/Products/Operations
 * show category rankings as plain text tables, hiding relative magnitude).
 *
 * Optionally pairs with DonutChart behind a bars⇄donut toggle when the values
 * are additive (shares of a whole) — mirroring the drill's catKind toggle.
 */

import { useState } from "react";
import { DonutChart } from "./DonutChart.js";
import { ChartTypeMenu } from "./ChartTypeMenu.js";

export interface RankedBarRow {
  key: string;
  value: number;
  /** Right-aligned secondary stat, e.g. "$276/cust". */
  sub?: string;
}

export function RankedBars({
  rows,
  fmt,
  onPick,
  donutToggle = false,
  defaultView = "bars",
  money = true,
  max,
  colorFor,
  baseline,
  loading = false,
}: {
  rows: RankedBarRow[];
  /** Value formatter for the right column. */
  fmt: (v: number) => string;
  /** Row click → usually a drill. Omit for static lists. */
  onPick?: (key: string) => void;
  /** Show the bars⇄donut toggle (only for additive values). */
  donutToggle?: boolean;
  /** Initial view (founder 2026-06-22: Top products defaults to donut/pie). */
  defaultView?: "bars" | "donut" | "table";
  money?: boolean;
  /** Cap rows shown as bars (rest fold into the donut's Other). */
  max?: number;
  /** Per-row bar color (e.g. green above / red below a baseline). Default accent. */
  colorFor?: (row: RankedBarRow) => string;
  /** Draw a vertical reference line across the tracks (e.g. the 1.0 baseline for
   *  an index like the Profit Efficiency Ratio). */
  baseline?: { value: number; label?: string };
  /** The selected period is still loading. Do not show a real empty state yet. */
  loading?: boolean;
}): React.ReactElement {
  const [view, setView] = useState<"bars" | "donut" | "table">(defaultView);
  const shown = max ? rows.slice(0, max) : rows;
  const barMax = Math.max(1, ...shown.map((r) => Math.abs(r.value)));

  // Chart-type menu (founder 2026-06-13): bars + table always; donut only when
  // values are additive shares (donutToggle). Always shown so every card offers
  // the choice.
  const opts = donutToggle
    ? [{ value: "bars", label: "Bars" }, { value: "donut", label: "Donut" }, { value: "table", label: "Table" }]
    : [{ value: "bars", label: "Bars" }, { value: "table", label: "Table" }];

  if (loading) {
    return <div role="status" style={{ color: "var(--faint)", fontSize: 12.5, padding: "18px 0" }}>Loading…</div>;
  }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 6 }}>
        <ChartTypeMenu options={opts}
          value={view} onChange={(v) => setView(v as "bars" | "donut" | "table")} />
      </div>
      {view === "donut" && donutToggle ? (
        <DonutChart money={money} onPick={onPick} onShowAll={() => setView("table")}
          slices={rows.map((r) => ({ key: r.key, value: r.value }))} />
      ) : view === "table" ? (
        <div style={{ maxHeight: 260, overflowY: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key} onClick={onPick ? () => onPick(r.key) : undefined}
                  className={onPick ? "dd-drill-row" : undefined} title={onPick ? "Click to drill in" : undefined}
                  style={{ cursor: onPick ? "pointer" : "default", borderTop: "1px solid var(--border)" }}>
                  <td style={{ padding: "6px 8px", color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 220 }} title={r.key}>{r.key}</td>
                  {r.sub !== undefined && <td className="dd-num" style={{ padding: "6px 8px", textAlign: "right", color: "var(--num)", fontSize: 11.5 }}>{r.sub}</td>}
                  <td className="dd-num" style={{ padding: "6px 8px", textAlign: "right", color: "var(--num)", fontWeight: 600 }}>{fmt(r.value)}</td>
                </tr>
              ))}
              {shown.length === 0 && <tr><td style={{ color: "var(--faint)", fontSize: 12.5, padding: "6px 8px" }}>No data yet.</td></tr>}
            </tbody>
          </table>
        </div>
      ) : (
        <div>
          {baseline && shown.length > 0 && (
            <div style={{ display: "flex", justifyContent: "flex-end", color: "var(--body-faint)", fontSize: 10.5, marginBottom: 4 }}>
              <span><i style={{ display: "inline-block", width: 2, height: 9, background: "var(--text)", opacity: 0.55, marginRight: 5, verticalAlign: -1 }} />{baseline.label ?? `baseline ${baseline.value}`}</span>
            </div>
          )}
          {shown.map((r) => (
            <div key={r.key} onClick={onPick ? () => onPick(r.key) : undefined}
              className={onPick ? "dd-drill-row" : undefined}
              title={onPick ? "Click to drill in" : undefined}
              style={{ display: "flex", alignItems: "center", gap: 12, padding: "5px 4px", borderRadius: 8, cursor: onPick ? "pointer" : "default" }}>
              <span style={{ width: 130, fontSize: 12.5, color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.key}>{r.key}</span>
              <div style={{ flex: 1, height: 20, background: "var(--surface-2)", borderRadius: 6, overflow: "hidden", position: "relative" }}>
                <div style={{ width: `${Math.max(1.5, (Math.abs(r.value) / barMax) * 100)}%`, height: "100%", background: colorFor?.(r) ?? "var(--accent)", borderRadius: 6 }} />
                {baseline && barMax > 0 && (
                  <div style={{ position: "absolute", top: -1, bottom: -1, left: `${Math.min(100, (baseline.value / barMax) * 100)}%`, width: 2, background: "var(--text)", opacity: 0.55 }} />
                )}
              </div>
              {r.sub !== undefined && <span className="dd-num" style={{ width: 92, textAlign: "right", color: "var(--num)", fontSize: 11.5 }}>{r.sub}</span>}
              <span className="dd-num" style={{ width: 84, textAlign: "right", color: "var(--num)", fontWeight: 600, fontSize: 12.5 }}>{fmt(r.value)}</span>
            </div>
          ))}
          {shown.length === 0 && <div style={{ color: "var(--faint)", fontSize: 12.5 }}>No data yet.</div>}
        </div>
      )}
    </div>
  );
}
